import { useCallback, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { ApolloError, MutationHookOptions, MutationTuple } from "@apollo/client";
import { LazyQueryResultTuple } from "@apollo/client/react/types/types";
import { FormikHelpers } from "formik";
import { GraphQLError } from "graphql/error/GraphQLError";
import { useNavigate } from "react-router";
import { v1 } from "uuid";
import { useSnackbar } from "@tecorien/mimic";
import { FormPageNavigationProps } from "components/layouts/FormPageLayout";
import { generateEditPath, generateViewPath } from "links";
import { EntityPageMode, useEntityPageParams } from "navigation";
import { serializeEntity } from "./serializer";

type Query = { __typename?: "Query", };
type Mutation = { __typename?: "Mutation" }
type MutationVariables<T> = { values: T }
type MutationResult<T> = {
  success: boolean
  data: T
}
type GetLazyQueryHook<TQuery extends Query> = () => LazyQueryResultTuple<TQuery, any >;
type CreateMutationHook<TMutationData extends Mutation, TMutationVariables extends MutationVariables<T>, T extends object> = (baseOptions?: MutationHookOptions<TMutationData, TMutationVariables>) => MutationTuple<TMutationData, any>;
type UpdateMutationHook<TMutationData extends Mutation, TMutationVariables extends MutationVariables<T>, T extends object> = (baseOptions?: MutationHookOptions<TMutationData, TMutationVariables>) => MutationTuple<TMutationData, any>;
type DefaultOnSubmitOptions<TMutationData extends Mutation, TMutationVariables extends MutationVariables<T>, T extends object, TMutationResult extends MutationResult<T>> = {
  createMutationHook: CreateMutationHook<TMutationData, TMutationVariables, T>
  updateMutationHook: UpdateMutationHook<TMutationData, TMutationVariables, T>
  basePath: string,
  formDataSerializer?: (values: T) => void,
  mutationDataExtractor?: (data: TMutationData) => TMutationResult
};
type ReadonlyEntityFormOptions<TQuery extends Query, T extends object> =
  {
    getLazyQueryHook: GetLazyQueryHook<TQuery>,
    dataExtractor?: (data: TQuery) => T
  };
type DefaultEntityFormOptions<TQuery extends Query, TMutationData extends Mutation, TMutationVariables extends MutationVariables<T>, T extends object, TMutationResult extends MutationResult<T>> =
  ReadonlyEntityFormOptions<TQuery, T>
  & DefaultOnSubmitOptions<TMutationData, TMutationVariables, T, TMutationResult>;

const defaultDataExtractor = (data: any) => Object.values(data || {})[0] as any;

export const setBackendErrors = (errors: ReadonlyArray<GraphQLError> | undefined, formikHelpers: FormikHelpers<any>) => {
  if (errors?.length) {
    errors
      .filter(error => error.extensions?.classification === "ValidationError")
      .filter(error => (error.extensions?.path as string)?.startsWith("data."))
      .reverse()
      .forEach(error => {
        formikHelpers.setFieldError(
          (error.extensions?.path as string).replace("data.", ""),
          error.message,
        );
      });
  }
};

export function useOnSubmit<T extends object>(
  submit: (values: T, formikHelpers: FormikHelpers<T>) => void,
) {
  const [ id ] = useState(v1());
  const { push } = useSnackbar();

  return useCallback(async (values: T, formikHelpers: FormikHelpers<T>) => {
    try {
      await submit(values, formikHelpers);
    } catch (error: any) {
      if (!(error instanceof ApolloError)) {
        push({
          type: "error",
          key: id,
          title: "Submit error",
          message: error?.message || JSON.stringify(error),
        });
      }
    }
  }, [ submit, push, id ]);
}

export function useSimpleOnSubmit<T extends object>(
  { submit, onSuccess }: {
    submit: (
      values: T,
      formikHelpers: FormikHelpers<T>,
      setResultsToForm: (success: boolean | null | undefined, entity: T | null | undefined, errors?: ReadonlyArray<GraphQLError>) => void,
    ) => void,
    onSuccess?: (entity: T) => void,
  },
) {
  return useOnSubmit(useCallback(async (values: T, formikHelpers: FormikHelpers<T>) => {
    const setResultsToForm = (success: boolean | null | undefined, entity: T | null | undefined, errors?: ReadonlyArray<GraphQLError>) => {
      setBackendErrors(errors, formikHelpers);
      if (success && entity) {
        if (flushSync) {
          // React 18 fix
          flushSync(() => formikHelpers.resetForm({ values: entity }));
        } else {
          formikHelpers.resetForm({ values: entity });
        }
        onSuccess?.(entity);
      }
    };
    await submit(values, formikHelpers, setResultsToForm);
  }, [ submit, onSuccess ]));
}

export function useDefaultOnSubmit<T extends object,
  TMutationData extends Mutation,
  TMutationVariables extends MutationVariables<T>,
  TMutationResult extends MutationResult<T>
>
({ formDataSerializer = serializeEntity, mutationDataExtractor = defaultDataExtractor, ...options }: DefaultOnSubmitOptions<TMutationData, TMutationVariables, T, TMutationResult>) {
  const { mode } = useEntityPageParams();
  const navigate = useNavigate();
  const [ create ] = options.createMutationHook();
  const [ update ] = options.updateMutationHook();

  const onSuccess = useCallback((entity: T) => {
    const getEntityLink = (entity: T) => generateViewPath(options.basePath, entity);
    if (mode === EntityPageMode.edit) {
      navigate(getEntityLink(entity), { replace: true });
    } else {
      navigate(getEntityLink(entity));
    }
  }, [ navigate, options.basePath, mode ]);

  const submit = useCallback(async (values: T, formikHelpers: FormikHelpers<T>, setResultsToForm: (success: boolean | null | undefined, entity: T | null | undefined, errors?: ReadonlyArray<GraphQLError>) => void) => {
    if (mode === EntityPageMode.create || mode === EntityPageMode.clone) {
      const { errors, data } = await create({ variables: { values: formDataSerializer(values) } });
      if (data) {
        const value = mutationDataExtractor(data);
        setResultsToForm(value?.success, value?.data, errors);
      } else {
        setResultsToForm(false, null, errors);
      }
    } else if (mode === EntityPageMode.edit) {
      const { errors, data } = await update({ variables: { values: formDataSerializer(values) } });
      if (data) {
        const value = mutationDataExtractor(data);
        setResultsToForm(value?.success, value?.data, errors);
      } else {
        setResultsToForm(false, null, errors);
      }
    }
  }, [ mode, create, update, formDataSerializer, mutationDataExtractor ]);

  return useSimpleOnSubmit({ submit, onSuccess });
}


export function useReadOnlyEntityForm<T extends object, TQuery extends Query>
({ getLazyQueryHook, dataExtractor = defaultDataExtractor }: ReadonlyEntityFormOptions<TQuery, T>) {
  const params = useEntityPageParams();
  const [ fetch, { called, data, loading } ] = getLazyQueryHook();
  useEffect(() => {
    if (params.id) {
      fetch({ variables: { id: params.id } });
    }
  }, [ fetch, params.id ]);

  const entity = dataExtractor(data!)

  return {
    data,
    entity,
    params,
    isFetching: (!!params.id && !called) || loading,
    notFound: !!params.id && called && !loading && !entity,
  };
}

export function useDefaultEntityForm<T extends object,
  TMutationData extends Mutation,
  TMutationVariables extends MutationVariables<T>,
  TMutationResult extends MutationResult<T>,
  TQuery extends Query
>
({ getLazyQueryHook, dataExtractor = defaultDataExtractor, ...restOptions }: DefaultEntityFormOptions<TQuery, TMutationData, TMutationVariables, T, TMutationResult>) {
  const params = useEntityPageParams();

  return {
    ...useReadOnlyEntityForm({ getLazyQueryHook, dataExtractor }),
    onSubmit: useDefaultOnSubmit(restOptions),
    navigation: useMemo((): FormPageNavigationProps => ({
      view: generateViewPath(restOptions.basePath, { id: params.id }),
      edit: generateEditPath(restOptions.basePath, { id: params.id }),
      list: restOptions.basePath,
    }), [ restOptions.basePath, params.id ]),
  };
}
